import { defineStore } from "pinia";

import { __bucket } from "~/stores/bucket.store";
import { __pbUser } from "~/stores/pb-user.store";
import { __user } from "~/stores/user.store";

import { clearCacheForRoute, fetchWithCache } from "~/utils/fetch";

import { routesCeph, routesPB } from "~/routes/declarations";

import type { EditableIdentifier, GetIdentifiersOptions, Identifier } from "~/types/identifiants";
import type { PBCollection } from "~/types/pb/collections";
import { Sub } from "~/types/subscriptions";

import { BYTE_MULTIPLIER, BYTE_POW_TO, IDENTIFIER_QUOTA_MAX_DEFAULT } from "~/composables/config";
import { formatSizeUnits } from "~/composables/units";

export const __identifier = defineStore("identifiersStore", {
	state: (): {
        identifiers: Map<PBCollection.Customer["id"], Identifier[]>;
        loaded: boolean;
    } => ({
		identifiers: new Map<PBCollection.Customer["id"], Identifier[]>(),
		loaded: false
	}),
	getters: {
		isLoading: state => !state.loaded,
		getIdentifierById:
            state => (id: string): Identifier | undefined => {
            	return state.identifiers.get(__clients().getClientId)?.find((identifier: Identifier) => identifier.id === id);
            },
		getBaseId (bucketsName: string[] = []): Identifier {
			return {
				id: "identifiant_leviia_" + getToken(TOKEN_LENGTH),
				description: "ID Random " + getToken(Math.ceil(TOKEN_LENGTH / 4)),
				accessKey: "cléacces_leviia_" + getToken(TOKEN_LENGTH),
				secretKey: "cléacces_leviia_" + getToken(TOKEN_LENGTH),
				// eslint-disable-next-line no-magic-numbers
				active: Math.random() > 0.5,
				buckets: bucketsName,
				objectsAmount: 0,
				spaceUsed: 0,
				hasQuota: false,
				quota: 0,
				isQuotaUnlimited: false,
				size: 0,
				objects: 0,
				isLegacy: false
			} as Identifier;
		},
		getActiveNonLegacyIdentifiers (): Identifier[] {
			return this.identifiers.get(__clients().getClientId)?.filter((identifier: Identifier) => identifier.active && !identifier.isLegacy);
		},
		getTotalIdentifiersQuota (): number {
			return this.identifiers.get(__clients().getClientId)?.reduce(
				(total: number, identifier: Identifier) => {
					const quota = identifier.quota ?? 0;
					return (
						total
                        + Math.ceil(parseFloat(formatSizeUnits(quota, "TO", true) + "")
                            * 100)
                        / 100
					);
				},
				0
			) ?? 0;
		},
		getTotalIdentifiersObjects (): number {
			return this.identifiers.get(__clients().getClientId)?.reduce(
				(total, identifier) => total + identifier.objects,
				0
			);
		},
		getBucketAmount (): number {
			let total = 0;

			this.identifiers.get(__clients().getClientId)?.forEach((id: Identifier) => {
				total += id.buckets.length ?? 0;
			});

			return total;
		},
		getIdentifiersAmount (): number {
			return this.identifiers.get(__clients().getClientId)?.length ?? 0;
		},
		getTotalSpaceUsed (): number {
			return this.identifiers.get(__clients().getClientId)?.reduce(
				(
					total: any,
					identifier: { spaceUsed: any; }
				) => total + identifier.spaceUsed,
				0
			);
		},
		getTotalQuota (): number {
			return this.identifiers.get(__clients().getClientId)?.reduce(
				(
					total: any,
					identifier: { quota: any; }
				) => total + identifier.quota,
				0
			);
		}
	},
	actions: {
		/**
         * Retrieves identifiers from PB.
         *
         * The method waits for subscription to Abonnements.S3, then fetches the full list of IdentifiantS3 from the 'objectStorage_identifiers' collection in PB. If the list is empty, the 'loaded' property
         * is set to true and resolves the Promise.
         * Otherwise, it fetches each identifier by ID and stores the resolved promises in an array. Finally, it filters out any null values and assigns the filtered array to the 'identifiers
         *' property.
         *
         * @returns A Promise that resolves to void.
         */
		async getIdentifiersFromPB (): Promise<void> {
			try {
				await waitForSubscription(Sub.Type.ObjectStorage);
			} catch (err) {
				console.warn("Error waiting for subscription", err);
				return;
			}
			const ids = await PB.i
				.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
				.getFullList({
					expand: "cluster"
				});

			const clientIdentifiers = new Map<Identifier["id"], PBCollection.IdentifiantS3["id"]>();
			ids.forEach((id: PBCollection.IdentifiantS3) => {
				clientIdentifiers.set(id.identifier, id.subscription);
			});

			if (!ids || ids.length === 0) {
				this.loaded = true;
				console.warn("No identifiers found");
				return Promise.resolve();
			}

			const promises: Promise<Identifier | undefined>[] = [];
			ids.forEach((id: PBCollection.IdentifiantS3) => {
				if (!id.expand.cluster) return;
				promises.push(this.getIdentifierFromCeph(
					id.identifier,
					id.expand.cluster.region,
					id.expand.cluster.instance,
					id.subscription
				));
			});

			const resolvedPromises = await Promise.all(promises);
			resolvedPromises.forEach((identifier: Identifier) => {
				const clientId = clientIdentifiers.get(identifier.id);
				if (!this.identifiers.has(clientId)) this.identifiers.set(clientId, []);
				// check if not already in the list
				if (!this.identifiers.get(clientId).find((id: Identifier) => id.id === identifier.id)) {
					this.identifiers.get(clientId).push(identifier);
				}
			});

			await waitForClients();
			this.identifiers.get(__clients().getClientId)?.forEach((identifier: Identifier) => {
				const id = ids.find((id: PBCollection.IdentifiantS3) => id.identifier === identifier.id);
				if (id) {
					identifier.quota = (id.quotaStorage ?? 0) * Math.pow(BYTE_MULTIPLIER, 4);
					identifier.hasQuota = (id.quotaStorage ?? 0) !== -1;
					identifier.isQuotaUnlimited = !identifier.hasQuota;
					identifier.active = id.isActivated;
					identifier.isBlocked = !id.isLeviiaActivated;

					if (id.expand && id.expand.cluster) {
						const cluster = id.expand.cluster;
						identifier.region = cluster.region;
						identifier.cluster = cluster.instance;
						identifier.pbCluster = cluster.id;
						identifier.endpoint = cluster.endpoint;
						identifier.growth = cluster.isGrowth ?? false;
						identifier.isLegacy = cluster.isLegacy ?? false;
						identifier.identifierQuotaMax = cluster.identifierQuotaMax ?? IDENTIFIER_QUOTA_MAX_DEFAULT;
					}
				}
			});

			this.loaded = true;
		},
		/**
         * Retrieves an Identifier from a given identifier and baseCluster.
         *
         * @param {string} identifier - The identifier to retrieve.
         * @param {string} region - The region of the identifier.
         * @param {string} cluster - The cluster of the identifier.
         * @param {string} clientId - The client ID associated with the operation.
         * @returns {Promise<Identifier | null>} - The retrieved Identifier or null if not found.
         */
		async getIdentifierFromCeph (
			identifier: string,
			region: string,
			cluster: string,
			clientId: string | undefined = undefined
		): Promise<Identifier | undefined> {
			clientId = clientId || __clients().getClientId;
			if (!region || !cluster) {
				const id = await PB.i.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
					.getFirstListItem(`identifier = '${identifier}'`, {
						expand: "cluster",
						fetch: fetchWithCache
					});

				const clusterObj = id?.expand?.cluster;
				region = clusterObj?.region ?? null;
				const clusterInstance = clusterObj?.instance ?? null;
				if (!region || !clusterInstance) {
					console.error("No cluster found for identifier: ", identifier);
					return undefined;
				}
				cluster = clusterInstance;
			}

			const id = await useFetchRoute<Identifier>(routesCeph.getIdentifiant, {
				region,
				cluster,
				identifiant: identifier
			});

			if (!id || Object.keys(id).length === 0) {
				console.warn("Identifier not found: ", identifier);
				return undefined;
			}

			if (id!.display_name === "" && id!.uid === "") {
				console.error(
					`Error: Identifier ${identifier} cannot be retrieved by server`,
					id
				);
				return undefined;
			}

			const tmpIdentifier = this.cephIDToLocalID(id, cluster, region);
			if (!tmpIdentifier) {
				console.error("Error saving identifier from Ceph");
				return undefined;
			}

			tmpIdentifier.buckets = await this.getBucketsListFromID(
				tmpIdentifier,
				region,
				cluster,
				clientId
			);

			return tmpIdentifier;
		},
		/**
         * Retrieves a list of buckets from the given identifier.
         *
         * @param {Identifier} identifier - The identifier to get the buckets from.
         * @param {string} region - The region of the identifier.
         * @param {string} cluster - The cluster of the identifier.
         * @param {string} clientId - The client ID associated with the operation.
         * @returns {Promise<string[]>} A promise that resolves to an array of bucket names.
         */
		async getBucketsListFromID (
			identifier: Identifier,
			region: string,
			cluster: string,
			clientId: string
		): Promise<string[]> {
			if (!identifier) {
				console.error("No identifier provided");
				return [];
			}

			const idBuckets = await useFetchRoute<string[]>(
				routesCeph.getBucketsOfIdentifiant,
				{
					region,
					cluster,
					identifiant: identifier.id
				}
			);

			idBuckets.forEach(bucket => {
				__bucket().addToLocalBucketList(bucket, clientId);
			});

			return idBuckets;
		},
		/**
         * Converts a CephID to a LocalID.
         *
         * @param wrapperId - The CephID object to convert.
         * @param cluster - The cluster identifier. Defaults to null if not provided.
         * @param region - The region identifier. Defaults to null if not provided.
         *
         * @returns The converted LocalID in the form of an Identifier object.
         */
		cephIDToLocalID (
			wrapperId: any,
			cluster: string | number | null = null,
			region: string | null = null
		): Identifier | null {
			__user().incrementUserS3Stats(
				{
					size: (wrapperId?.stats?.size_kb_actual ?? 0) * BYTE_MULTIPLIER,
					objects: wrapperId?.stats?.num_objects ?? 0
				},
				wrapperId.uid
			);

			const idCluster: string = cluster ?? wrapperId.cluster ?? "1";
			const idRegion: string = region ?? wrapperId.region ?? "fr";

			if (wrapperId.keys.length === 0) {
				console.error("No keys found (secret_key, access_key)");
				return null;
			}

			return {
				id: wrapperId.uid,
				description: wrapperId.display_name,
				accessKey: wrapperId.keys[0]["access_key"],
				secretKey: wrapperId.keys[0]["secret_key"],
				active: wrapperId.suspended === 0,
				spaceUsed: (wrapperId.stats?.size_kb_actual ?? 0) * BYTE_MULTIPLIER,
				hasQuota: wrapperId["user_quota"].enabled,
				quota: wrapperId["user_quota"].max_size,
				isQuotaUnlimited: !wrapperId["user_quota"].enabled,
				objectsAmount: 0,
				buckets: [],
				size: (wrapperId?.stats?.size_kb_actual ?? 0) * BYTE_MULTIPLIER,
				objects: wrapperId?.stats?.num_objects ?? 0,
				cluster: idCluster,
				region: idRegion,
				isLegacy: false // Will be set later from cluster data
			} as unknown as Identifier;
		},
		/**
         * Retrieves the identifiers based on the provided options.
         * If the identifiers are already loaded, it returns the cached values.
         * If the identifiers are still loading, it waits for them to finish and then returns an empty array.
         * If pagination options are provided, it returns a subset of the identifiers based on the page and limit.
         * If no pagination options are provided, it returns all the identifiers.
         *
         * @param {GetIdentifiersOptions} options - The options to customize the retrieval of the identifiers.
         * @returns {Promise<Identifier[]>} - A promise that resolves to an array of identifiers.
         */
		async getIdentifiers (options: GetIdentifiersOptions = {}): Promise<Identifier[]> {
			await this.getIdentifiersFromPB();

			let result: Identifier[];

			if (options?.page && options?.limit) {
				const start = (options.page - 1) * options.limit;
				const end = start + options.limit;
				result = this.identifiers.get(__clients().getClientId)
					.toSorted((a: Identifier, b: Identifier) => a?.description?.localeCompare(b?.description))
					.slice(start, end);
			} else {
				result = this.identifiers.get(__clients().getClientId);
			}

			return result;
		},
		/**
         * Calculates the number of pages needed to display identifiers based on a given limit.
         *
         * @param {number} limit - The maximum number of identifiers to display on each page.
         * @returns {number} - The total number of pages required to display all identifiers.
         */
		getIdentifiersPages (limit: number): number {
			return Math.ceil(this.identifiers.get(__clients().getClientId)?.length / limit);
		},
		/**
         * Remove a specified bucket name from all identifiers.
         *
         * @param {string} bucketName - The name of the bucket to be removed.
         *
         * @return {undefined}
         */
		removeBucketToAllIdenfifier (bucketName: string): void {
			clearCacheForRoute(routesCeph.getIdentifiant);
			this.identifiers.get(__clients().getClientId)?.forEach((identifiant: Identifier) => {
				const index = identifiant.buckets.findIndex(b => b === bucketName);
				identifiant.buckets.splice(index, 1);
			});
		},
		/**
         * Returns the identifier associated with a given bucket.
         * If not found locally, refreshes identifiers from PB and retries.
         *
         * @param {string} bucket - The bucket to search for.
         * @returns {Promise<Identifier|null>} - The identifier associated with the bucket, or null if not found.
         */
		async getIdentifierOfBucket (bucket: string): Promise<Identifier | null> {
			const clientId = __clients().getClientId;
			let id = (
				this.identifiers.get(clientId)?.find((identifier: {
                    buckets: string | string[]
                }) => identifier?.buckets?.includes(bucket)) ?? null
			);

			if (!id) {
				// Refresh identifiers from PB and retry
				await this.refreshIdentifiers();
				id = (
					this.identifiers.get(clientId)?.find((identifier: {
                        buckets: string | string[]
                    }) => identifier?.buckets?.includes(bucket)) ?? null
				);
			}

			return id;
		},
		/**
         * Creates a new identifier.
         *
         * @param {Partial<Identifier>} identifier - The partial identifier object.
         * @return {Promise<void>} - A promise that resolves when the identifier is created.
         */
		async createIdentifier (
			identifier: Partial<Identifier>,
			zone: string
		): Promise<void> {
			clearCacheForRoute(routesCeph.getIdentifiant);
			this.loaded = false;
			const aboS3 = __clients().getClientId ?? await __pbUser().getAboS3ID();
			if (!aboS3) {
				console.error("No objectStorage_subscriptions found");
				return;
			}

			const newIdCreate = {
				displayName: identifier.description,
				objectStorageSubscriptionId: aboS3,
				quota:
                    identifier.hasQuota && identifier.quota
                    	? identifier.quota / Math.pow(BYTE_MULTIPLIER, 4)
                    	: -1
			};
			const newId: Omit<PBCollection.IdentifiantS3, "expand"> & {
                cluster: string
            } = await useFetchRoute(routesPB.objectStorageIdentifiers, { zone }, newIdCreate);
			const cluster = await PB.i
				.collection<PBCollection.Clusters>("clusters")
				.getOne(newId.cluster);

			if (!newId) {
				console.error("Error creating new identifier");
				return;
			}

			const cephId = cluster
				? await this.getIdentifierFromCeph(
					newId.identifier,
					cluster.region,
					cluster.instance
				)
				: undefined;
			if (!cephId) {
				console.error("Error creating new identifier");
				return;
			}

			cephId.quota = newId.quotaStorage * Math.pow(BYTE_MULTIPLIER, BYTE_POW_TO);
			cephId.hasQuota = newId.quotaStorage !== -1;
			cephId.isQuotaUnlimited = !cephId.hasQuota;
			cephId.active = newId.isActivated;

			if (cluster) {
				cephId.region = cluster.region;
				cephId.cluster = cluster.instance;
				cephId.pbCluster = cluster.id;
				cephId.endpoint = cluster.endpoint;
			}

			this.identifiers.get(__clients().getClientId)?.push(cephId);

			this.loaded = true;
		},
		async deleteIdentifier (identifierId: string) {
			clearCacheForRoute(routesCeph.getIdentifiant);
			const index = this.identifiers.get(__clients().getClientId)?.findIndex((identifier: {
                id: string
            }) => identifier.id === identifierId);
			const identifier = this.getIdentifierById(identifierId);
			const identifiantS3 = await PB.i
				.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
				.getFirstListItem(`identifier = '${identifierId}'`, { fetch: fetchWithCache });

			if (!identifier || identifier.buckets.length > 0 || !identifiantS3) {
				if (identifier && identifier.buckets.length > 0) {
					console.error("Error: Identifier cannot be deleted because it has existing buckets");
				} else {
					console.error("Error: Identifier not found");
				}
				return;
			}

			await PB.i
				.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
				.delete(identifiantS3.id);

			this.identifiers.get(__clients().getClientId)?.splice(index, 1);
		},
		async addBucketToIdenfifier (identifierId: string, bucketName: string) {
			await this.getIdentifiers();
			const identifier = this.getIdentifierById(identifierId);

			if (!identifier) {
				return;
			}

			if (!identifier.buckets) {
				identifier.buckets = [];
			}

			if (identifier.buckets.includes(bucketName)) {
				return;
			}

			identifier.buckets.push(bucketName);
		},
		async editIdentifier (identifierId: string, newValues: EditableIdentifier) {
			clearCacheForRoute(routesCeph.getIdentifiant);
			const identifiantS3 = await PB.i
				.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
				.getFirstListItem(`identifier = '${identifierId}'`, { fetch: fetchWithCache });

			if (!identifiantS3) {
				console.error("Identifier not found");
				return;
			}

			PB.i
				.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
				.update(identifiantS3.id, {
					displayName: newValues.description,
					isActivated: newValues.active,
					quotaStorage:
                        newValues.hasQuota && newValues.quota
                        	? newValues.quota / Math.pow(BYTE_MULTIPLIER, 4)
                        	: -1
				})
				.then(() => {
					const identifier = this.getIdentifierById(identifierId);
					if (!identifier) {
						return;
					}

					identifier.active = newValues.active;
					identifier.description = newValues.description;
					identifier.hasQuota = newValues.hasQuota;
					identifier.quota = identifier.hasQuota ? newValues.quota : 0;
				})
				.catch((err: any) => {
					console.error("Error updating identifier", err);
				});
		},
		async setActive (identifierId: string, active: boolean) {
			clearCacheForRoute(routesCeph.getIdentifiant);
			const identifiantS3 = await PB.i
				.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
				.getFirstListItem(`identifier ='${identifierId}'`, { fetch: fetchWithCache });

			if (!identifiantS3) {
				console.error("Identifier not found");
				return;
			}

			try {
				await PB.i
					.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
					.update(identifiantS3.id, {
						isActivated: active
					});

				const identifier = this.getIdentifierById(identifierId);
				if (identifier) {
					identifier.active = active;
				}

				return identifier;
			} catch (err) {
				console.error("Error updating identifier", err);
			}
		},
		async refreshIdentifiers () {
			clearCacheForRoute(routesCeph.getIdentifiant);
			await this.getIdentifiersFromPB();
		},
		hasClusters (clusters: string[]): boolean {
			const clusterSet = new Set(this.identifiers.get(__clients().getClientId)?.map((identifier: Identifier) => identifier.region + identifier.cluster));

			return clusters.some(cluster => clusterSet.has(cluster));
		},
		async fetchIdentifierById (identifierId: string): Promise<Identifier | undefined> {
			await waitForSubscription(Sub.Type.ObjectStorage);
			const id = await PB.i
				.collection<PBCollection.IdentifiantS3>("objectStorage_identifiers")
				.getFirstListItem(`identifier = '${identifierId}'`, { expand: "cluster", fetch: fetchWithCache });

			if (!id) {
				console.warn("Identifier not found: ", identifierId);
				return undefined;
			}

			const cephId = await this.getIdentifierFromCeph(
				id.identifier,
				id.expand.cluster.region,
				id.expand.cluster.instance
			);

			if (!cephId) {
				console.warn("Identifier not found: ", identifierId);
				return undefined;
			}

			cephId.quota = (id.quotaStorage ?? 0) * Math.pow(BYTE_MULTIPLIER, BYTE_POW_TO);
			cephId.hasQuota = (id.quotaStorage ?? 0) !== -1;
			cephId.isQuotaUnlimited = !cephId.hasQuota;
			cephId.active = id.isActivated;

			const clientId = __clients().getClientId;
			this.createMissingClient(clientId);
			if (!this.identifiers.get(clientId).find((i: Identifier) => i.id === cephId.id)) {
				this.identifiers.get(clientId).push(cephId);
			}

			return cephId;
		},
		getEndpoint (identifierId: string): string {
			if (!identifierId) {
				console.error("No identifierId provided");
				return "";
			}

			const identifier = this.getIdentifierById(identifierId);
			if (!identifier) {
				console.error("No identifier found");
				return "";
			}

			return identifier.endpoint ?? "";
		},
		createMissingClient (id: string) {
			if (!this.identifiers.has(id)) {
				this.identifiers.set(id, []);
			}
		},
		/**
         * Récupère tous les clusters depuis PocketBase et retourne toutes les zones avec leur statut de disponibilité et leur flag
         * Filtre les zones où tous les clusters sont en legacy
         *
         * @returns {Promise<{zone: string, flag: string, isGrowth: boolean, isLegacy: boolean, identifierQuotaMax: number}[]>} - Liste des zones disponibles avec leur statut
         */
		async getAllClusters (): Promise<{
            zone: string,
            flag: string,
            isGrowth: boolean,
            isLegacy: boolean,
            identifierQuotaMax: number
        }[]> {
			try {
				const clusters = await PB.i
					.collection<PBCollection.Clusters>("clusters")
					.getFullList();

				// Récupère toutes les zones avec leur statut isGrowth et isLegacy
				const zonesMap = new Map<string, {
                    flag: string,
                    isGrowth: boolean,
                    isLegacy: boolean,
                    allLegacy: boolean,
                    clusterCount: number,
                    legacyCount: number,
                    identifierQuotaMax: number
                }>();

				clusters.forEach(cluster => {
					if (cluster.zone) {
						// Détermine le flag basé sur la région
						const flag = cluster.region?.toLowerCase().includes("fr") ? "fr" : cluster.region;

						// Si la zone existe déjà, met à jour les statuts
						const existingZone = zonesMap.get(cluster.zone);
						if (existingZone) {
							const isGrowth = existingZone.isGrowth || cluster.isGrowth;
							const isLegacy = existingZone.isLegacy || cluster.isLegacy;
							const clusterCount = existingZone.clusterCount + 1;
							const legacyCount = existingZone.legacyCount + (cluster.isLegacy ? 1 : 0);
							const allLegacy = legacyCount === clusterCount;
							// Prendre le min des identifierQuotaMax pour les clusters non-legacy et isGrowth (pour respecter les limites de tous les clusters de la zone)
							const identifierQuotaMax = !cluster.isLegacy && cluster.isGrowth
								? Math.min(existingZone.identifierQuotaMax, cluster.identifierQuotaMax ?? IDENTIFIER_QUOTA_MAX_DEFAULT)
								: existingZone.identifierQuotaMax;

							zonesMap.set(cluster.zone, {
								flag,
								isGrowth,
								isLegacy,
								allLegacy,
								clusterCount,
								legacyCount,
								identifierQuotaMax
							});
						} else {
							zonesMap.set(cluster.zone, {
								flag,
								isGrowth: cluster.isGrowth,
								isLegacy: cluster.isLegacy,
								allLegacy: cluster.isLegacy,
								clusterCount: 1,
								legacyCount: cluster.isLegacy ? 1 : 0,
								// Utiliser Infinity pour les clusters legacy ou non-growth afin que Math.min prenne le plus petit des non-legacy et growth
								identifierQuotaMax: (!cluster.isLegacy && cluster.isGrowth) ? (cluster.identifierQuotaMax ?? IDENTIFIER_QUOTA_MAX_DEFAULT) : Infinity
							});
						}
					}
				});

				// Filtrer les zones où tous les clusters sont en legacy
				return Array.from(zonesMap.entries())
					.filter(([ _zone, zoneData ]) => {
						return !zoneData.allLegacy;
					})
					.map(([ zone, { flag, isGrowth, isLegacy, identifierQuotaMax } ]) => ({
						zone,
						flag,
						isGrowth,
						isLegacy,
						identifierQuotaMax
					}))
					.sort((a, b) => a.zone.localeCompare(b.zone));
			} catch (error) {
				console.error("Erreur lors de la récupération des clusters:", error);
				return [];
			}
		}
	}
});
